import React from "react";
import { Card, Typography, Tag } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { getFormattedAmount, getFormattedPercentage } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";

const { Title, Text } = Typography;

const BinanceBalanceCard = ({
    current,
    roi,
    currency = "USDT",
    label = "Account balance",
    badgeText = null,
    showLiveDot = false,
    invested,
    netPl,
    realizedPl,
    cutoffLabel,
}) => {
    const roiValue = Number.isFinite(Number(roi)) ? Number(roi) : 0;
    const isPositive = roiValue >= 0;
    const symbol = getCurrencySymbol(currency);
    const effectiveBadgeText =
        badgeText && badgeText !== "Binance live" ? badgeText : null;
    const shouldShowLiveDot = Boolean(showLiveDot && effectiveBadgeText);

    const positiveColor = Tokens.positive || Tokens.profit;
    const negativeColor = Tokens.negative || Tokens.loss;

    const metrics = [];
    if (invested !== undefined && invested !== null) {
        metrics.push({
            key: "invested",
            label: "Invested",
            value: `${symbol}${getFormattedAmount(
                Number(invested) || 0,
                true,
                2
            )}`,
            color: Tokens.textPrimary,
        });
    }
    if (netPl !== undefined && netPl !== null) {
        const numericNetPl = Number(netPl) || 0;
        let netPlColor = Tokens.textSecondary;
        if (numericNetPl > 0) netPlColor = positiveColor;
        if (numericNetPl < 0) netPlColor = negativeColor;
        metrics.push({
            key: "netPl",
            label: "Open P&L",
            value: `${
                numericNetPl >= 0 ? "+" : "-"
            }${symbol}${getFormattedAmount(Math.abs(numericNetPl), true, 2)}`,
            color: netPlColor,
        });
    }
    if (
        Number.isFinite(Number(realizedPl)) &&
        (Number(realizedPl) !== 0 || Boolean(cutoffLabel))
    ) {
        const numericRealized = Number(realizedPl);
        metrics.push({
            key: "realizedPl",
            label: "Realized P&L",
            value: `${
                numericRealized >= 0 ? "+" : "-"
            }${symbol}${getFormattedAmount(
                Math.abs(numericRealized),
                true,
                2
            )}`,
            color: numericRealized >= 0 ? positiveColor : negativeColor,
        });
    }

    return (
        <Card
            style={{
                backgroundColor: Tokens.card,
                border: `1px solid ${Tokens.hairline}`,
                borderRadius: Tokens.radiusPanel,
                marginBottom: "20px",
                boxShadow: "none",
                position: "relative",
                overflow: "hidden",
            }}
            bodyStyle={{
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "12px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Text
                    style={{
                        color: Tokens.textSecondary,
                        fontSize: "13px",
                        fontWeight: 400,
                    }}
                >
                    {label}
                </Text>
                {effectiveBadgeText && (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            color: Tokens.textMuted,
                            fontSize: "12px",
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        {shouldShowLiveDot && (
                            <span
                                style={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    backgroundColor: Tokens.accent,
                                }}
                            />
                        )}
                        {effectiveBadgeText}
                    </span>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
            >
                <Title
                    level={1}
                    style={{
                        color: Tokens.textPrimary,
                        margin: 0,
                        fontWeight: 500,
                        fontSize: "32px",
                        lineHeight: "1.1",
                        letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {symbol}
                    {getFormattedAmount(current || 0, true, 2)}
                </Title>

                <Tag
                    style={{
                        backgroundColor: isPositive
                            ? Tokens.positiveBg
                            : Tokens.negativeBg,
                        color: isPositive ? positiveColor : negativeColor,
                        border: `1px solid ${
                            isPositive
                                ? Tokens.positiveBorder
                                : Tokens.negativeBorder
                        }`,
                        borderRadius: Tokens.radiusTag,
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontWeight: 500,
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        margin: 0,
                    }}
                >
                    {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {getFormattedPercentage(roiValue, 1)} ROI
                </Tag>
            </div>

            {metrics.length > 0 && (
                <div
                    style={{
                        borderTop: `1px solid ${Tokens.hairline}`,
                        paddingTop: "14px",
                        marginTop: "4px",
                        display: "grid",
                        gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))`,
                        gap: "12px",
                    }}
                >
                    {metrics.map((metric) => (
                        <div key={metric.key} style={{ minWidth: 0 }}>
                            <div
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "11px",
                                    marginBottom: "2px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {metric.label}
                            </div>
                            <div
                                style={{
                                    color: metric.color,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {metric.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};

export default BinanceBalanceCard;
